import { Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Lock } from "lucide-react";
import { I18nProvider } from "@/providers/i18n-provider";
import { AnonymousExperimentProvider } from "@/providers/experiment-provider";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { ThemeApplier } from "@/providers/theme-applier";

/**
 * Brand header for the logged-out claim flow. The public routes render outside
 * the app shell, so the page would otherwise have no logo/chrome — which reads
 * as an untrusted payment link. Uses the same /logo.png as the auth screens.
 */
function ClaimHeader() {
  const { t } = useTranslation();
  return (
    <header className="border-b border-border bg-background">
      <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
        <a href="https://speakai.co" className="inline-flex">
          <img
            src="/logo.png"
            alt="Speak AI"
            className="h-7 w-auto object-contain dark:invert"
            fetchPriority="high"
          />
        </a>
        <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <Lock className="h-3.5 w-3.5" aria-hidden="true" />
          {t("SPECIAL_OFFERS.SECURE_CHECKOUT", "Secure checkout")}
        </span>
      </div>
    </header>
  );
}

function ClaimFooter() {
  const { t } = useTranslation();
  return (
    <footer className="border-t border-border bg-background">
      <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-2 px-4 py-5 text-xs text-muted-foreground">
        <span>© {new Date().getFullYear()} Speak AI</span>
        <nav className="flex gap-4">
          <a
            href="https://www.speakai.co/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="transition-colors hover:text-foreground"
          >
            {t("SPECIAL_OFFERS.FOOTER_TERMS", "Terms")}
          </a>
          <a
            href="https://www.speakai.co/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="transition-colors hover:text-foreground"
          >
            {t("SPECIAL_OFFERS.FOOTER_PRIVACY", "Privacy")}
          </a>
          <a
            href="https://docs.speakai.co/help/"
            target="_blank"
            rel="noopener noreferrer"
            className="transition-colors hover:text-foreground"
          >
            {t("SPECIAL_OFFERS.FOOTER_SUPPORT", "Support")}
          </a>
        </nav>
      </div>
    </footer>
  );
}

/**
 * Provider shell for the public /claim-offer routes. Mounted OUTSIDE
 * RootProviders so logged-out visitors are never bounced to /auth/login by
 * AuthProvider's session guard. Wraps its own I18nProvider (translations resolve
 * without the authed provider tree) and ErrorBoundary; QueryProvider and
 * ToastProvider already wrap the whole router in app.tsx.
 */
export function ClaimOfferLayout() {
  return (
    <I18nProvider>
      <AnonymousExperimentProvider>
        <ErrorBoundary>
          <ThemeApplier />
          <div className="flex min-h-screen flex-col bg-background">
            <ClaimHeader />
            <main className="flex-1">
              <Outlet />
            </main>
            <ClaimFooter />
          </div>
        </ErrorBoundary>
      </AnonymousExperimentProvider>
    </I18nProvider>
  );
}
